import { useCallback, useEffect, useRef, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import {
  buildYearOptions,
  defaultPurchaseUploadYear,
} from './purchaseTrendPage';
import type {
  ParsePurchaseFileRequest,
  ParsePurchaseFileResponse,
  PurchaseUploadIssue,
  UnmatchedFileSupplier,
  UnmatchedGentlySupplier,
} from '@erp/rpc';
import FileUploadDropzone from '@/components/documents/FileUploadDropzone';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { WarningIcon } from '@phosphor-icons/react';
import type { UploadProgress } from 'src/types/UserDocuments';
import {
  useImportPurchaseFile,
  useParsePurchaseFile,
} from '@/api/rpc/hooks/rebates/usePurchaseUpload';

export const PURCHASE_FILE_ACCEPT = '.xlsx,.xls,.xlsm';

const EMPTY_UPLOAD_PROGRESS: UploadProgress[] = [];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Could not read file'));
        return;
      }
      const encoded = reader.result.split(',')[1];
      if (!encoded) {
        reject(new Error('Could not read file'));
        return;
      }
      resolve(encoded);
    };
    reader.readAsDataURL(file);
  });
}

function hasBlockingIssues(issues: PurchaseUploadIssue[]): boolean {
  return issues.some((issue) => issue.severity === 'error');
}

function UnmatchedFileTable({ rows }: { rows: UnmatchedFileSupplier[] }) {
  if (rows.length === 0) {
    return null;
  }
  return (
    <div data-testid="unmatched-in-file">
      <h3 className="mb-2 text-sm font-medium">
        <T context="heading for suppliers in the purchases file but not in Gently">
          In file, not in Gently
        </T>
      </h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <T context="table column for supplier number">Supplier number</T>
            </TableHead>
            <TableHead>
              <T context="table column for supplier name">Supplier</T>
            </TableHead>
            <TableHead>
              <T context="table column for row count">Rows</T>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.supplierCode}-${row.supplierName}`}>
              <TableCell>{row.supplierCode}</TableCell>
              <TableCell>{row.supplierName}</TableCell>
              <TableCell>{row.rowCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function UnmatchedGentlyTable({ rows }: { rows: UnmatchedGentlySupplier[] }) {
  const missingNumber = rows.filter(
    (row) => row.reason === 'missing_corporate_supplier_number',
  );
  const notInFile = rows.filter((row) => row.reason === 'number_not_in_file');
  if (rows.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3" data-testid="unmatched-in-gently">
      {missingNumber.length > 0 ? (
        <div data-testid="unmatched-no-supplier-number">
          <h3 className="mb-2 text-sm font-medium">
            <T context="heading for rebate suppliers missing a corporate supplier number">
              Rebate suppliers with no supplier number
            </T>
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <T context="table column for supplier name">Supplier</T>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {missingNumber.map((row) => (
                <TableRow key={row.supplierId}>
                  <TableCell>{row.supplierName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
      {notInFile.length > 0 ? (
        <div data-testid="unmatched-number-not-in-file">
          <h3 className="mb-2 text-sm font-medium">
            <T context="heading for rebate suppliers whose number is absent from the file">
              Supplier number not in file
            </T>
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <T context="table column for supplier number">
                    Supplier number
                  </T>
                </TableHead>
                <TableHead>
                  <T context="table column for supplier name">Supplier</T>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notInFile.map((row) => (
                <TableRow key={row.supplierId}>
                  <TableCell>{row.supplierCode}</TableCell>
                  <TableCell>{row.supplierName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}

function PurchaseUploadPreview({
  preview,
}: {
  preview: ParsePurchaseFileResponse;
}) {
  const checksumWarnings = preview.validationIssues.filter(
    (issue) => issue.type === 'checksum_mismatch',
  );
  return (
    <div className="space-y-4" data-testid="purchase-upload-preview">
      <p className="text-sm text-muted-foreground">
        <T context="purchase upload preview totals">
          Year <Var>{preview.year}</Var>, through month{' '}
          <Var>{preview.throughMonth}</Var>, <Var>{preview.storedRowCount}</Var>{' '}
          rows.
        </T>
      </p>
      {hasBlockingIssues(preview.validationIssues) ? (
        <AlertContainer>
          <WarningIcon className="h-4 w-4" />
          <AlertTitle>
            <T context="alert title when purchase upload has blocking row errors">
              Blocking row issues
            </T>
          </AlertTitle>
          <AlertDescription>
            <T context="alert body when purchase upload cannot be imported">
              Fix the errors below before replacing this year&apos;s purchases.
            </T>
          </AlertDescription>
        </AlertContainer>
      ) : (
        <AlertContainer>
          <WarningIcon className="h-4 w-4" />
          <AlertTitle>
            <T context="alert title that import replaces the year">
              This import replaces the year
            </T>
          </AlertTitle>
          <AlertDescription>
            <T context="alert body that import replaces stored purchases">
              Confirming will replace all stored purchases for{' '}
              <Var>{preview.year}</Var>.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}
      {preview.validationIssues.length > 0 ? (
        <div data-testid="purchase-upload-issues">
          <h3 className="mb-2 text-sm font-medium">
            <T context="heading for per-row purchase upload issues">
              Per-row issues
            </T>
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <T context="table column for issue severity">Severity</T>
                </TableHead>
                <TableHead>
                  <T context="table column for spreadsheet row">Row</T>
                </TableHead>
                <TableHead>
                  <T context="table column for issue message">Issue</T>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.validationIssues.map((issue, index) => (
                <TableRow key={`${issue.type}-${issue.rowNumber}-${index}`}>
                  <TableCell>{issue.severity}</TableCell>
                  <TableCell>{issue.rowNumber ?? '—'}</TableCell>
                  <TableCell>{issue.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
      {checksumWarnings.length > 0 ? (
        <p className="text-sm" data-testid="checksum-warnings">
          <T context="notice that checksum warnings were found in the purchases file">
            Checksum warnings are listed with the per-row issues.
          </T>
        </p>
      ) : null}
      <UnmatchedFileTable rows={preview.unmatched.inFileNotInGently} />
      <UnmatchedGentlyTable rows={preview.unmatched.rebateSuppliersNotInFile} />
      {preview.unmatchedDivisionNames.length > 0 ? (
        <div data-testid="unmatched-divisions">
          <h3 className="mb-2 text-sm font-medium">
            <T context="heading for unmatched division names in the purchases file">
              Unmatched divisions
            </T>
          </h3>
          <ul className="list-disc pl-5 text-sm">
            {preview.unmatchedDivisionNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function isImportBlocked(
  preview: ParsePurchaseFileResponse | null,
  pendingFile: { fileName: string; xlsxBase64: string } | null,
  importPending: boolean,
): boolean {
  return (
    !preview ||
    !pendingFile ||
    hasBlockingIssues(preview.validationIssues) ||
    importPending
  );
}

function useParseInitialFile({
  open,
  initialFile,
  parseFiles,
}: {
  open: boolean;
  initialFile: File | null | undefined;
  parseFiles: (files: File[]) => Promise<void>;
}) {
  const startedFileRef = useRef<File | null>(null);
  useEffect(() => {
    if (!open || !initialFile || startedFileRef.current === initialFile) {
      return;
    }
    startedFileRef.current = initialFile;
    void parseFiles([initialFile]);
  }, [initialFile, open, parseFiles]);
}

export function PurchaseUploadDialog({
  open,
  onOpenChange,
  onImported,
  initialFile,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialFile?: File | null;
  onImported: (input: {
    fileName: string;
    uploadedAt: Date;
    throughMonth: number;
    year: number;
  }) => void;
}) {
  const gt = useGT();
  const now = new Date();
  const yearOptions = buildYearOptions(
    [now.getFullYear(), now.getFullYear() - 1],
    now.getFullYear(),
  );
  const [uploadYear, setUploadYear] = useState(() =>
    defaultPurchaseUploadYear(now),
  );
  const parseMutation = useParsePurchaseFile();
  const importMutation = useImportPurchaseFile();
  const [preview, setPreview] = useState<ParsePurchaseFileResponse | null>(
    null,
  );
  const [pendingFile, setPendingFile] = useState<{
    fileName: string;
    xlsxBase64: string;
  } | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPreview(null);
    setPendingFile(null);
    setParseError(null);
    setUploadYear(defaultPurchaseUploadYear(new Date()));
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        reset();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, reset],
  );

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) {
        return;
      }
      setParseError(null);
      try {
        const xlsxBase64 = await fileToBase64(file);
        const request: ParsePurchaseFileRequest = {
          xlsxBase64,
          year: uploadYear,
          fileName: file.name,
        };
        const result = await parseMutation.mutateAsync(request);
        setPendingFile({ fileName: file.name, xlsxBase64 });
        setPreview(result);
      } catch (error) {
        setPreview(null);
        setPendingFile(null);
        setParseError(
          error instanceof Error
            ? error.message
            : gt('Could not parse the purchases file.', {
                context: 'toast when purchases file parse fails',
              }),
        );
      }
    },
    [gt, parseMutation, uploadYear],
  );

  useParseInitialFile({
    open,
    initialFile,
    parseFiles: handleFilesSelected,
  });

  const handleConfirm = useCallback(async () => {
    if (
      !pendingFile ||
      !preview ||
      hasBlockingIssues(preview.validationIssues)
    ) {
      return;
    }
    const result = await importMutation.mutateAsync({
      xlsxBase64: pendingFile.xlsxBase64,
      year: uploadYear,
      fileName: pendingFile.fileName,
    });
    onImported({
      fileName: pendingFile.fileName,
      uploadedAt: new Date(),
      throughMonth: result.throughMonth,
      year: result.year,
    });
    reset();
    onOpenChange(false);
  }, [
    importMutation,
    onImported,
    onOpenChange,
    pendingFile,
    preview,
    reset,
    uploadYear,
  ]);

  const confirmDisabled = isImportBlocked(
    preview,
    pendingFile,
    importMutation.isPending,
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title for uploading rebate supplier purchases">
              Upload Purchases
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description for purchases file import">
              Preview the file, review unmatched suppliers and row issues, then
              confirm to replace this year&apos;s purchases.
            </T>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col max-w-[180px]">
          <label className="text-sm font-medium mb-1.5">
            {gt('Upload year', {
              context: 'year of the purchases file being uploaded',
            })}
          </label>
          <Combobox
            items={yearOptions}
            getValue={(option) => option.id}
            getLabel={(option) => option.label}
            value={String(uploadYear)}
            onValueChange={(_id, item) => {
              if (item) {
                setUploadYear(item.value);
              }
            }}
            allowClear={false}
            highlightFirst={false}
            closeButton
            modal={false}
            className="h-8 justify-between font-normal"
            labels={pickerLabels({
              placeholder: gt('Select year', {
                context: 'placeholder for purchases upload year',
              }),
              search: 'Search years...',
            })}
          />
        </div>
        <FileUploadDropzone
          onFilesSelected={handleFilesSelected}
          uploadProgress={EMPTY_UPLOAD_PROGRESS}
          isUploading={parseMutation.isPending || importMutation.isPending}
          maxFiles={1}
          accept={PURCHASE_FILE_ACCEPT}
          fillAvailableHeight={false}
        />
        {parseError ? (
          <p className="text-sm text-destructive">{parseError}</p>
        ) : null}
        {preview ? <PurchaseUploadPreview preview={preview} /> : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            <T context="button to close the purchases upload dialog">Cancel</T>
          </Button>
          <Button
            onClick={() => {
              void handleConfirm();
            }}
            disabled={confirmDisabled}
            data-testid="confirm-purchase-import"
          >
            <T context="button to confirm replacing the year of purchases">
              Confirm import
            </T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
